import { useEffect } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate'

export default function Login() {
  const { t, i18n } = useTranslation()
  const { login, user, loading } = useAuth()
  const navigate = useLocalizedNavigate()
  const [searchParams] = useSearchParams()

  const { lang } = useParams<{ lang: string }>()
  const currentLang = lang || i18n.language || 'he'

  useEffect(() => { document.title = `${t('auth.signIn')} | ${t('common.appName')}` }, [t])
  const redirect = searchParams.get('redirect') || '/account'
  const error = searchParams.get('error')

  if (loading) return null

  if (user) {
    const target = redirect.startsWith(`/${currentLang}`) ? redirect : `/${currentLang}${redirect}`
    window.location.href = target
    return null
  }

  return (
    <div className="container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 10rem)' }}>
      <div style={{ maxWidth: '420px', width: '100%' }}>
        <div className="text-center mb-4">
          <h1>{t('auth.loginTitle')}</h1>
          <p className="text-gray">{t('auth.loginSubtitle')}</p>
        </div>

        {error && (
          <div className="card mb-4" style={{ background: '#fef2f2', borderColor: 'var(--danger)' }} role="alert">
            <p className="text-danger text-sm">{t('auth.loginError')}</p>
          </div>
        )}

        <div className="card">
          {/* Continue as Guest */}
          <button
            className="btn btn-primary btn-lg w-full"
            onClick={() => navigate('/create')}
            style={{ marginBottom: '0.5rem' }}
          >
            {t('auth.continueAsGuest')}
          </button>
          <p className="text-center text-xs text-gray" style={{ marginBottom: '1.5rem' }}>
            {t('auth.guestNote')}
          </p>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--gray-200)' }} />
            <span className="text-sm text-gray">{t('auth.orDivider')}</span>
            <div style={{ flex: 1, height: '1px', background: 'var(--gray-200)' }} />
          </div>

          {/* OAuth buttons */}
          <div className="auth-buttons">
            <button
              className="btn auth-btn auth-btn-google"
              onClick={() => login('google', redirect)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continueWith', { provider: 'Google' })}
            </button>

            <button
              className="btn auth-btn auth-btn-microsoft"
              onClick={() => login('microsoft', redirect)}
            >
              <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
                <rect fill="#f25022" x="1" y="1" width="10" height="10"/>
                <rect fill="#00a4ef" x="1" y="12" width="10" height="10"/>
                <rect fill="#7fba00" x="12" y="1" width="10" height="10"/>
                <rect fill="#ffb900" x="12" y="12" width="10" height="10"/>
              </svg>
              {t('auth.continueWith', { provider: 'Microsoft' })}
            </button>
          </div>

          <p className="text-center text-sm text-gray mt-4">
            {t('auth.freeDocNote')}
          </p>
        </div>
      </div>
    </div>
  )
}
